import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { format, isToday, isThisWeek, isThisMonth, isThisYear } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const PERIODOS = [
  { value: '',       label: 'Todos' },
  { value: 'hoje',   label: 'Hoje' },
  { value: 'semana', label: 'Semana' },
  { value: 'mes',    label: 'Mês' },
  { value: 'ano',    label: 'Ano' },
];

function dentroDoperiodo(servico, periodo) {
  if (!periodo) return true;
  const data = new Date(servico.updatedAt || servico.createdAt);
  if (periodo === 'hoje')   return isToday(data);
  if (periodo === 'semana') return isThisWeek(data, { weekStartsOn: 0 });
  if (periodo === 'mes')    return isThisMonth(data);
  if (periodo === 'ano')    return isThisYear(data);
  return true;
}

const STATUS = {
  orcamento:            { label: 'Ag. Avaliação do Mecânico', cor: 'bg-gray-100 text-gray-700' },
  aprovado:             { label: 'Aprovado - ag. mecânico', cor: 'bg-blue-100 text-blue-800' },
  em_andamento:         { label: 'Em andamento/Em conserto', cor: 'bg-orange-100 text-orange-800' },
  aguardando_orcamento: { label: 'Enviar orçamento p/ aprovação do cliente', cor: 'bg-purple-100 text-purple-800' },
  aguardando_cliente:   { label: 'Aguardando aprovação do cliente', cor: 'bg-cyan-100 text-cyan-800' },
  concluido:            { label: 'Concluído',              cor: 'bg-green-100 text-green-800' },
  cancelado:            { label: 'Orçamento Não Aprovado', cor: 'bg-red-100 text-red-800' },
};

export default function Servicos() {
  const [servicos, setServicos] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useAuth();
  const statusParam = searchParams.get('status') || '';
  const [filtroStatus, setFiltroStatus] = useState(statusParam);
  const [filtroPeriodo, setFiltroPeriodo] = useState('');
  const [busca, setBusca] = useState('');
  const [visualizacao, setVisualizacao] = useState(
    localStorage.getItem('servicos_view') || 'lista'
  );
  const [colunas, setColunas] = useState(
    Number(localStorage.getItem('servicos_colunas')) || 2
  );

  // Sincroniza filtro com parâmetro da URL (ex: navegação do Dashboard)
  useEffect(() => {
    setFiltroStatus(statusParam);
  }, [statusParam]);

  useEffect(() => {
    api.get('/servicos').then(r => setServicos(r.data));
  }, []);

  const alterarVisualizacao = modo => {
    setVisualizacao(modo);
    localStorage.setItem('servicos_view', modo);
  };

  const alterarColunas = n => {
    setColunas(n);
    localStorage.setItem('servicos_colunas', n);
  };

  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  }[colunas];

  const termo = busca.toLowerCase().trim();

  // Aplica filtro de período primeiro (base para contagens e lista)
  const servicosPorPeriodo = servicos.filter(s => dentroDoperiodo(s, filtroPeriodo));

  // Contagem por status dentro do período selecionado
  const contagemStatus = servicosPorPeriodo.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {});

  // Lista final: período + status + busca
  const servicosFiltrados = servicosPorPeriodo
    .filter(s => !filtroStatus || s.status === filtroStatus)
    .filter(s => !termo || (
      s.cliente?.nome?.toLowerCase().includes(termo) ||
      s.veiculo?.placa?.toLowerCase().includes(termo) ||
      s.veiculo?.modelo?.toLowerCase().includes(termo) ||
      s.veiculo?.marca?.toLowerCase().includes(termo) ||
      String(s.numero).includes(termo) ||
      s.diagnostico?.toLowerCase().includes(termo)
    ));

  return (
    <div className="max-w-5xl mx-auto">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Serviços</h1>
        <div className="flex items-center gap-2">
          {/* Seletor de colunas — só no modo cards */}
          {visualizacao === 'cards' && (
            <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
              {[1, 2, 3].map(n => (
                <button
                  key={n}
                  onClick={() => alterarColunas(n)}
                  title={`${n} coluna${n > 1 ? 's' : ''}`}
                  className={`w-8 h-8 rounded-md text-xs font-bold transition-colors ${colunas === n ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {n}
                </button>
              ))}
            </div>
          )}

          {/* Toggle lista / cards */}
          <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
            <button
              onClick={() => alterarVisualizacao('lista')}
              title="Visualização em lista"
              className={`p-2 rounded-md transition-colors ${visualizacao === 'lista' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={() => alterarVisualizacao('cards')}
              title="Visualização em cards"
              className={`p-2 rounded-md transition-colors ${visualizacao === 'cards' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
            </button>
          </div>
          <button onClick={() => navigate('/servicos/novo')} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700">
            + Novo
          </button>
        </div>
      </div>

      <div className="flex gap-5">
        {/* ── Sidebar de status ── */}
        <div className="w-52 shrink-0">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Período */}
            <div className="px-3 py-2.5 bg-gray-50 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Período</span>
            </div>
            <div className="p-2 space-y-0.5">
              {PERIODOS.map(({ value, label }) => (
                <button key={value} onClick={() => setFiltroPeriodo(value)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${filtroPeriodo === value ? 'bg-gray-700 text-white' : 'text-gray-700 hover:bg-gray-50'}`}>
                  {label}
                </button>
              ))}
            </div>

            {/* Status */}
            <div className="px-3 py-2.5 bg-gray-50 border-t border-b border-gray-100 mt-1">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</span>
            </div>
            <div className="p-2 space-y-0.5">
              {[{ key: '', label: 'Todos', cor: 'bg-gray-100 text-gray-700' },
                ...Object.entries(STATUS).filter(([k]) => k !== 'orcamento').map(([k, v]) => ({ key: k, label: v.label, cor: v.cor }))
              ].map(({ key, label, cor }) => {
                const count = key === '' ? servicosPorPeriodo.length : (contagemStatus[key] || 0);
                const ativo = filtroStatus === key;
                return (
                  <button key={key} onClick={() => setFiltroStatus(key)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${ativo ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}>
                    <span className="text-left leading-tight">{label}</span>
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ml-2 shrink-0 ${ativo ? 'bg-white/20 text-white' : cor}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Conteúdo principal ── */}
        <div className="flex-1 min-w-0">
          {/* Campo de busca */}
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
            </div>
            <input
              className="w-full border border-gray-300 rounded-xl pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
              placeholder="Buscar por nº OS, cliente, placa, modelo ou diagnóstico..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
            />
            {busca && (
              <button onClick={() => setBusca('')} className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Contador */}
          {(busca || filtroStatus) && (
            <p className="text-sm text-gray-500 mb-3 px-1">
              {servicosFiltrados.length === 0 ? 'Nenhum serviço encontrado'
                : `${servicosFiltrados.length} serviço${servicosFiltrados.length > 1 ? 's' : ''} encontrado${servicosFiltrados.length > 1 ? 's' : ''}`}
              {busca && <span className="text-gray-400"> para "<strong>{busca}</strong>"</span>}
            </p>
          )}

      {/* ── LISTA ─────────────────────────────────────────────────────────── */}
      {visualizacao === 'lista' && (
        <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-50">
          {servicosFiltrados.length === 0 && <p className="text-gray-400 text-center py-10">Nenhum serviço encontrado</p>}
          {servicosFiltrados.map(s => (
            <div key={s._id} className="p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors"
              onClick={() => navigate(`/servicos/${s._id}`, { state: { from: '/' } })}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    s.status === 'concluido' ? 'bg-green-500' :
                    s.status === 'em_andamento' ? 'bg-orange-400' :
                    s.status === 'aprovado' ? 'bg-blue-500' :
                    s.status === 'aguardando_orcamento' ? 'bg-purple-500' :
                    s.status === 'cancelado' ? 'bg-red-400' : 'bg-yellow-400'
                  }`} />
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-800">
                      <span className="text-gray-400 text-sm mr-1">#{s.numero}</span>
                      {s.cliente?.nome}
                    </div>
                    <div className="text-sm text-gray-500 truncate">
                      {s.veiculo?.placa} • {s.veiculo?.marca} {s.veiculo?.modelo} {s.veiculo?.ano}
                    </div>
                    {s.responsavel && <div className="text-xs text-blue-500 font-medium">🔧 {s.responsavel}</div>}
                    <div className="text-xs text-gray-400">{format(new Date(s.updatedAt || s.createdAt), "dd/MM/yyyy", { locale: ptBR })}</div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS[s.status]?.cor}`}>
                    {STATUS[s.status]?.label}
                  </span>
                  {isAdmin && (
                    <div className="text-sm font-semibold text-gray-700 mt-1">R$ {s.totalGeral?.toFixed(2)}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── CARDS ─────────────────────────────────────────────────────────── */}
      {visualizacao === 'cards' && (
        <>
          {servicosFiltrados.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">Nenhum serviço encontrado</div>
          )}
          <div className={`grid ${gridClass} gap-3`}>
            {servicosFiltrados.map(s => (
              <div key={s._id} onClick={() => navigate(`/servicos/${s._id}`, { state: { from: '/' } })}
                className="bg-white rounded-xl shadow-sm p-4 cursor-pointer hover:shadow-md active:scale-95 transition-all border border-gray-100">
                {/* Topo: número + status */}
                <div className="flex items-center justify-between mb-3">
                  <span className="font-bold text-gray-700 text-sm">OS #{s.numero}</span>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS[s.status]?.cor}`}>
                    {STATUS[s.status]?.label}
                  </span>
                </div>
                {/* Cliente */}
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-base">👤</span>
                  <span className="font-semibold text-gray-800 truncate">{s.cliente?.nome}</span>
                </div>
                {/* Veículo */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-base">🚗</span>
                  <span className="text-sm text-gray-500 truncate">
                    {s.veiculo?.placa} • {s.veiculo?.marca} {s.veiculo?.modelo} {s.veiculo?.ano}
                  </span>
                </div>
                {/* Mecânico */}
                {s.responsavel && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-sm">🔧</span>
                    <span className="text-xs font-medium text-blue-600">{s.responsavel}</span>
                  </div>
                )}
                {/* Rodapé */}
                <div className="flex items-center justify-between border-t border-gray-50 pt-2">
                  <span className="text-xs text-gray-400">
                    {format(new Date(s.updatedAt || s.createdAt), "dd/MM/yyyy", { locale: ptBR })}
                  </span>
                  {isAdmin && (
                    <span className="text-sm font-bold text-gray-800">R$ {s.totalGeral?.toFixed(2)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
        </div>{/* fim conteúdo principal */}
      </div>{/* fim flex sidebar+conteúdo */}
    </div>
  );
}
